import { useState, useEffect } from 'react';
import { getApiUrl } from '../lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { staggerContainer, staggerItem } from '../lib/animations';
import StrainCard from '../components/StrainCard';
import StrainDetailModal from '../components/StrainDetailModal';

interface Strain {
  name: string;
  type?: string;
  effects?: string[];
  flavors?: string[];
  description?: string;
  rating?: number;
  image_url?: string;
  medicalUses?: string[];
  negatives?: string[];
}

export default function Strains() {
  const [strains, setStrains] = useState<Strain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [selectedStrain, setSelectedStrain] = useState<Strain | null>(null);

  useEffect(() => {
    fetch(getApiUrl('/api/strains-data?per_page=500'))
      .then(r => r.json())
      .then(data => {
        const items = data.items ?? data;
        if (Array.isArray(items)) setStrains(items);
      })
      .catch((err) => {
        console.error('Failed to load strains:', err);
        setError('Failed to load strains. Please try again later.');
      })
      .finally(() => setLoading(false));
  }, []);

  const filtered = strains.filter(s => {
    const matchesSearch = (typeof s.name === 'string' ? s.name.toLowerCase() : '').includes(search.toLowerCase());
    const matchesType = typeFilter === 'all' || (typeof s.type === 'string' ? s.type.toLowerCase() : 'hybrid') === typeFilter.toLowerCase();
    return matchesSearch && matchesType;
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold text-cream mb-1">Strain Library</h2>
      <p className="text-cream/50 text-sm mb-6">Browse cannabis strains and learn about their effects</p>

      {/* Search + filter bar */}
      <div className="flex gap-3 mb-6">
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search strains..."
          className="flex-1 bg-cannabis-800 border border-cannabis-600 rounded-lg px-4 py-3 text-cream placeholder:text-cream/30 focus:outline-none focus:border-cannabis-400 text-sm"
        />
        <div className="flex gap-1 bg-cannabis-800 rounded-lg p-1 border border-cannabis-700">
          {['all', 'sativa', 'indica', 'hybrid'].map(t => (
            <button
              key={t}
              onClick={() => setTypeFilter(t)}
              className={`px-3 py-2 rounded text-xs font-medium transition capitalize ${
                typeFilter === t
                  ? t === 'sativa' ? 'bg-yellow-500/20 text-yellow-300'
                    : t === 'indica' ? 'bg-purple-500/20 text-purple-300'
                    : t === 'hybrid' ? 'bg-cannabis-500/20 text-cannabis-300'
                    : 'bg-cannabis-600 text-cream'
                  : 'text-cream/40 hover:text-cream'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Results count */}
      <p className="text-cream/30 text-xs mb-4">{filtered.length} strain{filtered.length !== 1 ? 's' : ''} found</p>

      {/* Error state */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      {/* Loading state */}
      {loading ? (
        <div className="text-center py-16">
          <p className="text-cream/50 text-sm">Loading strains...</p>
        </div>
      ) : error ? (
        <div className="text-center py-16">
          <p className="text-cream/50 text-sm">Unable to load strains at this time</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-cream/50 text-sm">No strains match your search</p>
        </div>
      ) : (
        /* Strain grid */
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          {filtered.map(strain => (
            <motion.div key={strain.name} variants={staggerItem}>
              <StrainCard
                strain={strain}
                onClick={() => setSelectedStrain(strain)}
              />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Detail modal */}
      <AnimatePresence>
        {selectedStrain && (
          <StrainDetailModal
            strain={selectedStrain}
            onClose={() => setSelectedStrain(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
